import { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Plus, Pencil, Trash2, Upload, Eye, EyeOff, Search } from 'lucide-react'
import { menusApi, pagesApi } from '../../services/api'
import Button from '../../components/ui/Button'
import LoadingSpinner from '../../components/ui/LoadingSpinner'
import Modal from '../../components/ui/Modal'
import RichTextEditor from '../../components/ui/RichTextEditor'
import AdminFormField, { adminInputClass } from '../../components/admin/AdminFormField'
import AdminFormSection from '../../components/admin/AdminFormSection'
import AdminImageField from '../../components/admin/AdminImageField'
import { previewImageSrc } from '../../utils/imagePreview'
import { matchesPageSearch, orderPagesByMenu } from '../../utils/adminPageOrder'
import { titleToPageSlug } from '../../utils/pageSlug'
import { STORY_CARD_TYPES } from '../../constants/storyCards'

const EMPTY_FORM = {
  slug: '',
  title_en: '',
  title_ar: '',
  excerpt_en: '',
  excerpt_ar: '',
  hero_label_en: '',
  hero_label_ar: '',
  content_en: '',
  content_ar: '',
  meta_title_en: '',
  meta_title_ar: '',
  meta_description_en: '',
  meta_description_ar: '',
  is_published: false,
  show_related_pages: false,
  story_card_type: '',
}

function normalizePage(page) {
  return {
    ...page,
    is_published: page.is_published ?? page.published ?? true,
    show_related_pages: Boolean(page.show_related_pages),
    story_card_type: page.story_card_type ?? '',
  }
}

function buildPagePayload(form, heroImageFile, removeHeroImage, pageImageFile, removePageImage) {
  const useFormData = Boolean(heroImageFile || removeHeroImage || pageImageFile || removePageImage)

  if (useFormData) {
    const fd = new FormData()
    const fields = [
      'slug',
      'title_en',
      'title_ar',
      'content_en',
      'content_ar',
      'excerpt_en',
      'excerpt_ar',
      'hero_label_en',
      'hero_label_ar',
      'meta_title_en',
      'meta_title_ar',
      'meta_description_en',
      'meta_description_ar',
    ]

    fields.forEach((field) => {
      fd.append(field, form[field] ?? '')
    })

    fd.append('is_published', form.is_published ? '1' : '0')
    fd.append('show_related_pages', form.show_related_pages ? '1' : '0')
    fd.append('story_card_type', form.story_card_type || '')

    if (heroImageFile) {
      fd.append('hero_image', heroImageFile)
    }
    if (removeHeroImage) {
      fd.append('remove_hero_image', '1')
    }
    if (pageImageFile) {
      fd.append('image', pageImageFile)
    } else if (form.image) {
      fd.append('image', form.image)
    }
    if (removePageImage) {
      fd.append('remove_image', '1')
    }

    return fd
  }

  return {
    slug: form.slug,
    title_en: form.title_en,
    title_ar: form.title_ar,
    content_en: form.content_en ?? '',
    content_ar: form.content_ar ?? '',
    excerpt_en: form.excerpt_en ?? '',
    excerpt_ar: form.excerpt_ar ?? '',
    hero_label_en: form.hero_label_en ?? '',
    hero_label_ar: form.hero_label_ar ?? '',
    meta_title_en: form.meta_title_en ?? '',
    meta_title_ar: form.meta_title_ar ?? '',
    meta_description_en: form.meta_description_en ?? '',
    meta_description_ar: form.meta_description_ar ?? '',
    is_published: Boolean(form.is_published),
    show_related_pages: Boolean(form.show_related_pages),
    story_card_type: form.story_card_type || null,
    ...(removeHeroImage ? { remove_hero_image: true } : {}),
    ...(removePageImage ? { remove_image: true } : {}),
    ...(form.image && !removePageImage ? { image: form.image } : {}),
  }
}

export default function AdminPages() {
  const { t } = useTranslation()
  const [pages, setPages] = useState([])
  const [loading, setLoading] = useState(true)
  const [form, setForm] = useState(null)
  const [heroImageFile, setHeroImageFile] = useState(null)
  const [heroImagePreview, setHeroImagePreview] = useState(null)
  const [removeHeroImage, setRemoveHeroImage] = useState(false)
  const [pageImageFile, setPageImageFile] = useState(null)
  const [pageImagePreview, setPageImagePreview] = useState(null)
  const [removePageImage, setRemovePageImage] = useState(false)
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState('')
  const [togglingId, setTogglingId] = useState(null)
  const [pageToDelete, setPageToDelete] = useState(null)
  const [deleting, setDeleting] = useState(false)
  const [searchInput, setSearchInput] = useState('')
  const [searchQuery, setSearchQuery] = useState('')
  const [menuTree, setMenuTree] = useState([])
  const heroFileRef = useRef(null)

  useEffect(() => {
    const timer = window.setTimeout(() => setSearchQuery(searchInput.trim()), 300)
    return () => window.clearTimeout(timer)
  }, [searchInput])

  useEffect(() => {
    menusApi
      .getAllAdmin()
      .then(({ data }) => setMenuTree(data.data || data || []))
      .catch(() => setMenuTree([]))
  }, [])

  const load = () => {
    setLoading(true)
    pagesApi
      .getAllAdmin({
        search: searchQuery || undefined,
        per_page: searchQuery ? undefined : 500,
      })
      .then(({ data }) => setPages(data.data || data || []))
      .catch(() => setPages([]))
      .finally(() => setLoading(false))
  }

  useEffect(load, [searchQuery])

  const displayPages = useMemo(() => {
    if (searchQuery) {
      return pages
        .filter((page) => matchesPageSearch(page, searchQuery))
        .map((page, index) => ({
          ...page,
          depth: 0,
          displayOrder: index + 1,
          inMenu: true,
        }))
    }

    return orderPagesByMenu(pages, menuTree)
  }, [pages, menuTree, searchQuery])

  useEffect(() => {
    return () => {
      if (heroImagePreview?.startsWith('blob:')) {
        URL.revokeObjectURL(heroImagePreview)
      }
    }
  }, [heroImagePreview])

  const resetHeroImageState = () => {
    if (heroImagePreview?.startsWith('blob:')) {
      URL.revokeObjectURL(heroImagePreview)
    }
    if (pageImagePreview?.startsWith('blob:')) {
      URL.revokeObjectURL(pageImagePreview)
    }
    setHeroImageFile(null)
    setHeroImagePreview(null)
    setRemoveHeroImage(false)
    setPageImageFile(null)
    setPageImagePreview(null)
    setRemovePageImage(false)
    if (heroFileRef.current) {
      heroFileRef.current.value = ''
    }
  }

  const handleHeroImageSelect = (e) => {
    const file = e.target.files?.[0]
    if (!file) return

    if (heroImagePreview?.startsWith('blob:')) {
      URL.revokeObjectURL(heroImagePreview)
    }

    setHeroImageFile(file)
    setHeroImagePreview(URL.createObjectURL(file))
    setRemoveHeroImage(false)
  }

  const handlePageImageSelect = (file) => {
    if (pageImagePreview?.startsWith('blob:')) {
      URL.revokeObjectURL(pageImagePreview)
    }
    setPageImageFile(file)
    setPageImagePreview(URL.createObjectURL(file))
    setRemovePageImage(false)
  }

  const handleRemovePageImage = () => {
    if (pageImagePreview?.startsWith('blob:')) {
      URL.revokeObjectURL(pageImagePreview)
    }
    setPageImageFile(null)
    setPageImagePreview(null)
    setRemovePageImage(true)
    setForm((current) => (current ? { ...current, image: '' } : current))
  }

  const handleRemoveHeroImage = () => {
    if (heroImagePreview?.startsWith('blob:')) {
      URL.revokeObjectURL(heroImagePreview)
    }
    setHeroImageFile(null)
    setHeroImagePreview(null)
    setRemoveHeroImage(true)
    if (heroFileRef.current) {
      heroFileRef.current.value = ''
    }
    setForm((current) => (current ? { ...current, hero_image: '' } : current))
  }

  const handleSave = async (e) => {
    e.preventDefault()
    setSaving(true)
    setSaveError('')
    try {
      const payload = buildPagePayload(form, heroImageFile, removeHeroImage, pageImageFile, removePageImage)
      if (form.id) await pagesApi.update(form.id, payload)
      else await pagesApi.create(payload)
      resetHeroImageState()
      setForm(null)
      load()
    } catch (err) {
      const message =
        err?.response?.data?.message ||
        Object.values(err?.response?.data?.errors || {})?.flat?.()?.[0] ||
        'Failed to save. Please try again.'
      setSaveError(message)
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async () => {
    if (!pageToDelete) return

    setDeleting(true)
    try {
      await pagesApi.delete(pageToDelete.id)
      setPageToDelete(null)
      load()
    } catch {
      setPageToDelete(null)
      load()
    } finally {
      setDeleting(false)
    }
  }

  const handleTogglePublished = async (page) => {
    setTogglingId(page.id)
    try {
      const payload = {
        slug: page.slug,
        title_en: page.title_en,
        title_ar: page.title_ar,
        content_en: page.content_en ?? '',
        content_ar: page.content_ar ?? '',
        excerpt_en: page.excerpt_en ?? '',
        excerpt_ar: page.excerpt_ar ?? '',
        hero_label_en: page.hero_label_en ?? '',
        hero_label_ar: page.hero_label_ar ?? '',
        meta_title_en: page.meta_title_en ?? '',
        meta_title_ar: page.meta_title_ar ?? '',
        meta_description_en: page.meta_description_en ?? '',
        meta_description_ar: page.meta_description_ar ?? '',
        is_published: !page.is_published,
        show_related_pages: Boolean(page.show_related_pages),
      }
      await pagesApi.update(page.id, payload)
      setPages((current) =>
        current.map((item) =>
          item.id === page.id ? { ...item, is_published: !page.is_published } : item,
        ),
      )
    } catch {
      load()
    } finally {
      setTogglingId(null)
    }
  }

  const openCreate = () => {
    setSaveError('')
    resetHeroImageState()
    setForm(EMPTY_FORM)
  }
  const openEdit = (page) => {
    setSaveError('')
    resetHeroImageState()
    setForm(normalizePage(page))
  }
  const closeForm = () => {
    setSaveError('')
    resetHeroImageState()
    setForm(null)
  }

  const currentPagePreview = removePageImage
    ? null
    : previewImageSrc(form?.image, pageImagePreview)

  const currentHeroPreview = removeHeroImage
    ? null
    : previewImageSrc(form?.hero_image, heroImagePreview)

  const updateFormField = (key, value) => {
    setForm((current) => {
      if (!current) return current

      if (key === 'title_en') {
        return {
          ...current,
          title_en: value,
          slug: titleToPageSlug(value),
        }
      }

      return { ...current, [key]: value }
    })
  }

  const heroFields = [
    { key: 'hero_label_en', label: t('admin.pages.heroLabelEn'), compact: true },
    { key: 'hero_label_ar', label: t('admin.pages.heroLabelAr'), ar: true, compact: true },
    { key: 'title_en', label: t('admin.pages.heroTitleEn'), required: true, compact: true },
    { key: 'title_ar', label: t('admin.pages.heroTitleAr'), ar: true, required: true, compact: true },
    { key: 'excerpt_en', label: t('admin.pages.heroTextEn'), textarea: true, compact: true },
    { key: 'excerpt_ar', label: t('admin.pages.heroTextAr'), textarea: true, ar: true, compact: true },
  ]

  const seoFields = [
    { key: 'meta_title_en', label: t('admin.pages.metaTitleEn') },
    { key: 'meta_title_ar', label: t('admin.pages.metaTitleAr'), ar: true },
    { key: 'meta_description_en', label: t('admin.pages.metaDescriptionEn'), textarea: true, span2: true },
    { key: 'meta_description_ar', label: t('admin.pages.metaDescriptionAr'), textarea: true, span2: true, ar: true },
  ]

  const renderFormField = ({ key, label, span2, required, ar, textarea, compact }) => {
    const fieldId = `page-${key}`
    const arProps = ar ? { dir: 'rtl', lang: 'ar' } : {}
    const inputClass = `${adminInputClass} ${compact ? 'py-2 text-sm' : ''} ${ar ? 'font-ar' : ''}`

    return (
      <AdminFormField
        key={key}
        label={label}
        htmlFor={fieldId}
        className={span2 ? 'sm:col-span-2' : ''}
      >
        {textarea ? (
          <textarea
            id={fieldId}
            value={form[key] || ''}
            onChange={(e) => updateFormField(key, e.target.value)}
            rows={compact ? 2 : 3}
            {...arProps}
            className={inputClass}
          />
        ) : (
          <input
            id={fieldId}
            value={form[key] ?? ''}
            onChange={(e) => updateFormField(key, e.target.value)}
            required={required}
            {...arProps}
            className={inputClass}
          />
        )}
      </AdminFormField>
    )
  }

  return (
    <div>
      <div className="mb-8 flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-primary">{t('admin.pages.title')}</h1>
          {!searchQuery ? (
            <p className="mt-1 text-sm text-text/60">{t('admin.pages.orderHint')}</p>
          ) : null}
        </div>
        <Button onClick={openCreate} variant="accent" size="sm">
          <Plus className="h-4 w-4" />
          {t('admin.pages.add')}
        </Button>
      </div>

      <div className="relative mb-6 max-w-md">
        <Search className="absolute start-3 top-1/2 h-4 w-4 -translate-y-1/2 text-text/40" />
        <input
          type="search"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          placeholder={t('admin.pages.searchPages')}
          aria-label={t('admin.pages.searchPages')}
          className="w-full rounded-xl border border-border bg-white py-2.5 ps-10 pe-4 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
        />
      </div>

      {searchQuery ? (
        <p className="mb-4 text-sm text-text/60">
          {t('admin.pages.searchResults', { count: displayPages.length, query: searchQuery })}
        </p>
      ) : null}

      <Modal
        isOpen={Boolean(form)}
        onClose={closeForm}
        title={form?.id ? t('admin.pages.edit') : t('admin.pages.add')}
        maxWidth="max-w-4xl"
      >
        {form && (
          <>
            <form onSubmit={handleSave} className="grid gap-4 sm:grid-cols-2">
              <AdminFormField
                label={t('admin.pages.slug')}
                htmlFor="page-slug"
                className="sm:col-span-2"
              >
                <input
                  id="page-slug"
                  value={form.slug ?? ''}
                  readOnly
                  className={`${adminInputClass} cursor-default bg-background text-text/70`}
                />
              </AdminFormField>

              <AdminFormSection
                title={t('admin.pages.heroSectionTitle')}
                description={t('admin.pages.heroSectionHint')}
              >
                <AdminFormField
                  label={t('admin.pages.heroImage')}
                  htmlFor="page-hero-image"
                  className="sm:col-span-2"
                >
                  <div className="space-y-3">
                    <p className="text-xs text-text/60">{t('admin.pages.heroImageHint')}</p>
                    {currentHeroPreview ? (
                      <div>
                        <p className="mb-2 text-xs text-text/60">{t('admin.pages.heroImagePreview')}</p>
                        <img
                          src={currentHeroPreview}
                          alt=""
                          className="h-40 w-full max-w-2xl rounded-xl border border-border object-cover"
                          onError={(e) => {
                            e.currentTarget.style.display = 'none'
                          }}
                        />
                      </div>
                    ) : null}
                    <div className="flex flex-wrap items-center gap-3">
                      <input
                        ref={heroFileRef}
                        id="page-hero-image"
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={handleHeroImageSelect}
                      />
                      <Button
                        type="button"
                        variant="outlineDark"
                        size="sm"
                        onClick={() => heroFileRef.current?.click()}
                        disabled={saving}
                      >
                        <Upload className="h-4 w-4" />
                        {heroImageFile ? heroImageFile.name : t('admin.pages.uploadHeroImage')}
                      </Button>
                      {(currentHeroPreview || form.hero_image) && !removeHeroImage ? (
                        <Button type="button" variant="ghost" size="sm" onClick={handleRemoveHeroImage}>
                          {t('admin.pages.removeHeroImage')}
                        </Button>
                      ) : null}
                    </div>
                  </div>
                </AdminFormField>

                {heroFields.map(renderFormField)}
              </AdminFormSection>

              <AdminImageField
                label={t('admin.pages.pageImage')}
                htmlFor="page-content-image"
                className="sm:col-span-2"
                value={form.image}
                onChange={(image) => setForm({ ...form, image })}
                imageFile={pageImageFile}
                imagePreview={currentPagePreview}
                onFileSelect={handlePageImageSelect}
                onRemove={handleRemovePageImage}
                disabled={saving}
              />

              <AdminFormField
                label={t('admin.pages.contentEn')}
                htmlFor="page-content-en"
                className="sm:col-span-2"
              >
                <RichTextEditor
                  value={form.content_en || ''}
                  onChange={(html) => setForm({ ...form, content_en: html })}
                  dir="ltr"
                  variant="page"
                  minHeight="320px"
                />
              </AdminFormField>

              <AdminFormField
                label={t('admin.pages.contentAr')}
                htmlFor="page-content-ar"
                className="sm:col-span-2"
              >
                <RichTextEditor
                  value={form.content_ar || ''}
                  onChange={(html) => setForm({ ...form, content_ar: html })}
                  dir="rtl"
                  variant="page"
                  minHeight="320px"
                />
              </AdminFormField>

              <AdminFormField
                label={t('admin.pages.storyCardType')}
                htmlFor="page-story-card-type"
                className="sm:col-span-2"
              >
                <select
                  id="page-story-card-type"
                  value={form.story_card_type ?? ''}
                  onChange={(e) => setForm({ ...form, story_card_type: e.target.value })}
                  className={`${adminInputClass} bg-white`}
                >
                  <option value="">{t('admin.pages.storyCardTypeNone')}</option>
                  {STORY_CARD_TYPES.map((type) => (
                    <option key={type} value={type}>
                      {t(`admin.storyCards.${type}Title`)}
                    </option>
                  ))}
                </select>
                <p className="mt-2 text-xs text-text/60">{t('admin.pages.storyCardTypeHint')}</p>
              </AdminFormField>

              <label className="flex items-center gap-2 text-sm sm:col-span-2">
                <input
                  type="checkbox"
                  checked={Boolean(form.show_related_pages)}
                  onChange={(e) => setForm({ ...form, show_related_pages: e.target.checked })}
                />
                {t('admin.pages.showRelatedPages')}
              </label>

              <AdminFormSection
                title={t('admin.pages.seoSectionTitle')}
                description={t('admin.pages.seoSectionHint')}
              >
                {seoFields.map(renderFormField)}
              </AdminFormSection>

              <div className="flex flex-wrap gap-2 sm:col-span-2">
                <Button type="submit" variant="primary" disabled={saving}>
                  {t('buttons.save')}
                </Button>
                <Button type="button" variant="ghost" onClick={closeForm}>
                  {t('buttons.cancel')}
                </Button>
              </div>
              {saveError && <p className="text-sm text-red-600 sm:col-span-2">{saveError}</p>}
            </form>
          </>
        )}
      </Modal>

      <Modal
        isOpen={Boolean(pageToDelete)}
        onClose={() => !deleting && setPageToDelete(null)}
        title={t('admin.pages.confirmDeleteTitle')}
        maxWidth="max-w-md"
      >
        {pageToDelete && (
          <div className="space-y-5">
            <p className="text-sm text-text/75">{t('admin.pages.confirmDelete')}</p>
            <p className="rounded-xl bg-red-50 px-4 py-3 text-sm font-medium text-red-800">
              {pageToDelete.title_en}
            </p>
            <div className="flex flex-wrap justify-end gap-2">
              <Button
                type="button"
                variant="ghost"
                onClick={() => setPageToDelete(null)}
                disabled={deleting}
              >
                {t('buttons.cancel')}
              </Button>
              <Button
                type="button"
                variant="primary"
                className="bg-red-600 hover:bg-red-700"
                onClick={handleDelete}
                disabled={deleting}
              >
                {deleting ? t('admin.pages.deleting') : t('buttons.delete')}
              </Button>
            </div>
          </div>
        )}
      </Modal>

      {loading ? (
        <LoadingSpinner className="py-20" />
      ) : displayPages.length === 0 ? (
        <p className="text-text/60">
          {searchQuery ? t('admin.pages.searchNoResults') : t('admin.table.noData')}
        </p>
      ) : (
        <div className="overflow-x-auto rounded-2xl border border-border bg-white">
          <table className="w-full text-sm">
            <thead className="border-b border-border bg-background">
              <tr>
                <th className="w-20 px-3 py-3 text-start font-semibold">{t('admin.pages.order')}</th>
                <th className="px-4 py-3 text-start font-semibold">{t('admin.pages.titleEn')}</th>
                <th className="px-4 py-3 text-start font-semibold">{t('admin.pages.titleAr')}</th>
                <th className="px-4 py-3 text-start font-semibold">{t('admin.pages.slug')}</th>
                <th className="px-4 py-3 text-end font-semibold">{t('admin.table.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {displayPages.map((page) => (
                <tr key={page.id} className="border-b border-border last:border-0">
                  <td className="px-3 py-3">
                    <span className="font-semibold tabular-nums text-primary">{page.displayOrder}</span>
                  </td>
                  <td className="px-4 py-3">
                    <div
                      className="flex items-center gap-2 font-medium text-primary"
                      style={{ paddingInlineStart: `${(page.depth ?? 0) * 1.25}rem` }}
                    >
                      {page.depth > 0 ? (
                        <span className="text-text/30" aria-hidden="true">
                          └
                        </span>
                      ) : null}
                      <span>{page.title_en}</span>
                      {!searchQuery && page.inMenu === false ? (
                        <span className="rounded bg-amber-50 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-amber-700">
                          {t('admin.pages.notInMenu')}
                        </span>
                      ) : null}
                    </div>
                  </td>
                  <td className="px-4 py-3 font-ar" dir="rtl" lang="ar">
                    {page.title_ar}
                  </td>
                  <td className="px-4 py-3 text-text/70">{page.slug}</td>
                  <td className="px-4 py-3">
                    <div className="flex justify-end gap-2">
                      <button
                        type="button"
                        onClick={() => openEdit(page)}
                        className="rounded-lg p-2 text-primary hover:bg-primary/5"
                        title={t('admin.pages.edit')}
                      >
                        <Pencil className="h-4 w-4" />
                      </button>
                      <button
                        type="button"
                        onClick={() => setPageToDelete(page)}
                        className="rounded-lg p-2 text-red-600 hover:bg-red-50"
                        title={t('buttons.delete')}
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                      <button
                        type="button"
                        onClick={() => handleTogglePublished(page)}
                        disabled={togglingId === page.id}
                        className={`rounded-lg p-2 ${
                          page.is_published
                            ? 'text-green-600 hover:bg-green-50'
                            : 'text-text/40 hover:bg-gray-100'
                        }`}
                        title={
                          page.is_published
                            ? t('admin.pages.unpublishPage')
                            : t('admin.pages.publishPage')
                        }
                      >
                        {page.is_published ? (
                          <Eye className="h-4 w-4" />
                        ) : (
                          <EyeOff className="h-4 w-4" />
                        )}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
